import React, { useCallback, useEffect, useState } from "react";
import { usePersistedPanelSize } from "../hooks/usePersistedPanelSize";

function useResizeEnabled(query = "(min-width: 1024px)") {
  const [enabled, setEnabled] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : true,
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const sync = () => setEnabled(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [query]);

  return enabled;
}

/**
 * Horizontal split: lead pane (fixed width) | drag handle | trail pane (flex).
 */
export default function AdminResizableSplit({
  storageKey,
  defaultSize = 220,
  minSize = 160,
  maxSize = 480,
  lead,
  trail,
  className = "",
  leadClassName = "",
  leadAriaLabel,
  trailClassName = "",
  stackedClassName = "admin-resize-split-stacked",
  disabled = false,
  hideLeadWhenDisabled = true,
}) {
  const resizeEnabled = useResizeEnabled();
  const active = resizeEnabled && !disabled && Boolean(lead);
  const { size, setClampedSize, reset } = usePersistedPanelSize(storageKey, {
    defaultSize,
    minSize,
    maxSize,
  });

  const onHandlePointerDown = useCallback(
    (event) => {
      if (!active) return;
      event.preventDefault();
      const startX = event.clientX;
      const startSize = size;
      document.body.classList.add("admin-is-resizing");

      const onMove = (ev) => {
        setClampedSize(startSize + (ev.clientX - startX));
      };
      const onUp = () => {
        document.body.classList.remove("admin-is-resizing");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [active, size, setClampedSize],
  );

  const showLead = Boolean(lead) && (!hideLeadWhenDisabled || active);

  return (
    <div
      className={`admin-resize-split ${active ? "admin-resize-split-active" : stackedClassName} ${className}`.trim()}
    >
      {showLead ? (
        <div
          className={`admin-resize-lead ${leadClassName}`.trim()}
          style={active ? { width: size, flexShrink: 0 } : undefined}
          aria-label={leadAriaLabel}
        >
          {lead}
        </div>
      ) : null}
      {active ? (
        <div
          className="admin-resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={size}
          aria-valuemin={minSize}
          aria-valuemax={maxSize}
          title="Drag to resize · double-click to reset"
          onPointerDown={onHandlePointerDown}
          onDoubleClick={reset}
        />
      ) : null}
      <div className={`admin-resize-trail ${trailClassName}`.trim()}>{trail}</div>
    </div>
  );
}
